import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { apiService } from "../services/apiService";
import { Select } from 'antd';

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

// Custom styles for Excel-like vertical borders
const gridStyles = `
  .ag-root {
    --ag-borders-side-color: #d0d0d0;
  }
  
  .ag-cell {
    border-right: 1px solid #d0d0d0 !important;
    border-bottom: 1px solid #d0d0d0 !important;
  }
  
  .ag-header-cell {
    border-right: 1px solid #d0d0d0 !important;
    border-bottom: 1px solid #d0d0d0 !important;
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
    color: #333 !important;
  }
  
  .ag-header-group-cell {
    border-right: 1px solid #d0d0d0 !important;
    border-bottom: 1px solid #d0d0d0 !important;
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
    color: #333 !important;
  }
  
  .ag-cell:last-child {
    border-right: 1px solid #d0d0d0 !important;
  }
  
  .ag-header-cell-text {
    overflow: visible !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = gridStyles;
  document.head.appendChild(styleSheet);
}

ModuleRegistry.registerModules([AllCommunityModule]);

const EditableRowSpanWithColumnGroup = () => {
   // Get current date to set default quarter and year
   const getCurrentQuarter = () => {
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    if (month <= 3) return 'Q1';
    if (month <= 6) return 'Q2';
    if (month <= 9) return 'Q3';
    return 'Q4';
   };

   const [filteredData, setFilteredData] = useState([]); // Store filtered data
      const [editingKey, setEditingKey] = useState(null); // Store the key of the row being edited
      const [dates, setDates] = useState([null, null]);
      const [loading, setLoading] = useState(true);
      const [data, setData] = useState([]); // Store the actual sales data
      const [total, setTotal] = useState(0); // Store total number of records for pagination
      const [currentPage, setCurrentPage] = useState(1); // Store current page for pagination     
      const [pageSize, setPageSize] = useState(10); // Store page size for pagination
      // Generate last 3 years dynamically
      const currentYear = new Date().getFullYear();
      const [availableYears, setAvailableYears] = useState([]);
      const [selectedYear, setSelectedYear] = useState(currentYear);
      const [selectedQuarter, setSelectedQuarter] = useState(['Q1']); // Array for multi-select

      const fetchSalesData = async (page,limit,startDate=null,endDate=null) => {
          setLoading(true); // Start loading
          try {
              const payload = {
                  page,
                  limit,
              };
              payload.record_type = 'actual_sales_summary';
              payload.year = selectedYear;
              payload.quarter = selectedQuarter;
              // Add date range to the payload if provided
              if (startDate && endDate) {
                  payload.startDate = startDate;
                  payload.endDate = endDate;
              }
             
      const response = await apiService(`actual-sales/get-summary-records-new`, "POST",payload);
              console.log("Fetched sales data:", response);
              
              // Group data by SBU code first
              const groupedBySBU = {};
              response.data.records.forEach((record) => {
                const sbuCode = record.sbu_code || "Unknown";
                if (!groupedBySBU[sbuCode]) {
                  groupedBySBU[sbuCode] = [];
                }
                groupedBySBU[sbuCode].push(record);
              });
              
              // Transform grouped data to grid format - ONE row per SBU+ItemType with rowSpan
              const transformedData = [];
              Object.entries(groupedBySBU).forEach(([sbuCode, sbuRecords]) => {
                // Group this SBU's records by item type
                const itemTypeMap = {};
                sbuRecords.forEach((record) => {
                  const itemType = record.item_type_name || "";
                  const itemTypeCode = record.item_type || "";
                  if (!itemTypeMap[itemType]) {
                    itemTypeMap[itemType] = {
                      itemType,
                      itemTypeCode,
                      records: []
                    };
                  }
                  itemTypeMap[itemType].records.push(record);
                });
                
                // Create rows for each item type, with rowSpan only on first item
                const itemTypeEntries = Object.entries(itemTypeMap);
                const rowSpanValue = itemTypeEntries.length;
                
                itemTypeEntries.forEach(([, itemTypeData], index) => {
                  const rowData = {
                    sbucode: index === 0 ? sbuCode : "", // Only set SBU code on first row
                    itemType: itemTypeData.itemType,
                    itemTypeCode: itemTypeData.itemTypeCode,
                    rowSpan: index === 0 ? rowSpanValue : undefined, // rowSpan only on first item
                  };
                  
                  // Map period data to month-specific fields
                  itemTypeData.records.forEach((record) => {
                    const month = record.month || record.period || "unknown";
                    const monthPrefix = getMonthPrefix(month);
                    
                    if (!rowData[`${monthPrefix}_invoiceAmount`]) {
                      rowData[`${monthPrefix}_invoiceAmount`] = 0;
                      rowData[`${monthPrefix}_firmPO`] = 0;
                      rowData[`${monthPrefix}_firmPORisk`] = 0;
                      rowData[`${monthPrefix}_lowRisk`] = 0;
                      rowData[`${monthPrefix}_highRisk`] = 0;
                      rowData[`${monthPrefix}_markupHistorical`] = 0;
                      rowData[`${monthPrefix}_mtbp2025`] = 0;
                      rowData[`${monthPrefix}_vsMtbp2025`] = 0;
                      rowData[`${monthPrefix}_fc6`] = 0;
                      rowData[`${monthPrefix}_vsFC6`] = 0;
                      rowData[`${monthPrefix}_apr24`] = 0;
                      rowData[`${monthPrefix}_vs24A`] = 0;
                    }
                    rowData[`${monthPrefix}_invoiceAmount`] = record.total_invoice_amount || 0;
                  });
                  
                  transformedData.push(rowData);
                });
              });
              
              setData(response.data.records);
              setGridRowData(transformedData);
              setFilteredData(response.data.records);
              setTotal(response.data.pagination.total);
       
          } catch (error) {
              console.error("Error fetching sales data:", error);
  
          } finally {
              setLoading(false); // Stop loading
          }
      };
      
      // Helper function to get month prefix from month name or number
      const getMonthPrefix = (month) => {
        const monthMap = {
          'april': 'apr',
          'may': 'may',
          'june': 'jun',
          'july': 'jul',
          'august': 'aug',
          'september': 'sep',
          'october': 'oct',
          'november': 'nov',
          'december': 'dec',
          'january': 'jan',
          'february': 'feb',
          'march': 'mar',
          'q1': 'q1',
          'q2': 'q2',
          'q3': 'q3',
          'q4': 'q4',
          '4': 'apr',
          '5': 'may',
          '6': 'jun',
          '7': 'jul',
          '8': 'aug',
          '9': 'sep',
          '10': 'oct',
          '11': 'nov',
          '12': 'dec',
          '1': 'jan',
          '2': 'feb',
          '3': 'mar',
        };
        return monthMap[String(month).toLowerCase()] || 'unknown';
      };

      const fetchYears = async () => {
        try {
          const response = await apiService('actual-sales/years', 'GET');
          console.log('Full API Response:', response);
          console.log('Response Data:', response.data);
          
          if (response.data && response.statusCode === 200) {
            const years = response.data || [];
            console.log('Fetched years:', years);
            
            setAvailableYears(years);
            if (years.length > 0) {
              setSelectedYear(years[0]);
            }
          }
        } catch (error) {
          console.error('Error fetching years:', error);
        }
      };

      useEffect(() => {
        fetchYears();
      }, []);

      useEffect(() => {
          fetchSalesData(currentPage, pageSize); // Fetch sales data when year/quarter changes
      }, [selectedYear, selectedQuarter]); // Trigger on year or quarter change
const gridRef = useRef(null);

  /* ===============================
     STATE (SOURCE OF TRUTH)
  =============================== */
  const [gridRowData, setGridRowData] = useState([]);

  /* ===============================
     COLUMN DEFINITIONS
  =============================== */
  const getQuarterColumns = (quarters, year) => {
    if (!quarters || quarters.length === 0) return [];
    
    const quarterMap = {
      'Q1': { months: ['apr', 'may', 'jun'], label: 'Q1 (Apr-Jun)' },
      'Q2': { months: ['jul', 'aug', 'sep'], label: 'Q2 (Jul-Sep)' },
      'Q3': { months: ['oct', 'nov', 'dec'], label: 'Q3 (Oct-Dec)' },
      'Q4': { months: ['jan', 'feb', 'mar'], label: 'Q4 (Jan-Mar)' },
    };
    
    const allMonthColumns = [];
    
    // Loop through each selected quarter
    quarters.forEach((quarter) => {
      const quarterData = quarterMap[quarter];
      if (!quarterData) return;
      
      const months = quarterData.months;
      
      // Create columns for all months in this quarter
      months.forEach((monthPrefix) => {
        // Determine the year suffix based on financial year
        // Q1, Q2, Q3 use current year, Q4 uses next year
        let yearSuffix;
        if (quarter === 'Q4') {
          // Jan, Feb, Mar are in next year
          yearSuffix = String(year + 1).slice(-2);
        } else {
          // Apr-Dec are in current year
          yearSuffix = String(year).slice(-2);
        }
        
        const monthLabels = {
          'apr': `April ${yearSuffix}`,
          'may': `May ${yearSuffix}`,
          'jun': `June ${yearSuffix}`,
          'jul': `July ${yearSuffix}`,
          'aug': `Aug ${yearSuffix}`,
          'sep': `Sept ${yearSuffix}`,
          'oct': `Oct ${yearSuffix}`,
          'nov': `Nov ${yearSuffix}`,
          'dec': `Dec ${yearSuffix}`,
          'jan': `Jan ${yearSuffix}`,
          'feb': `Feb ${yearSuffix}`,
          'mar': `Mar ${yearSuffix}`,
        };
        
        allMonthColumns.push({
          headerName: monthLabels[monthPrefix],
          marryChildren: true,
          children: [
            {
              headerName: "Actual",
              field: `${monthPrefix}_invoiceAmount`,
              editable: false,
              valueFormatter: (params) => `$${params.value?.toFixed(2) || 0}`,
            },
            {
              headerName: "Firm PO",
              field: `${monthPrefix}_firmPO`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: "Firm PO Risk",
              field: `${monthPrefix}_firmPORisk`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: "Low Risk",
              field: `${monthPrefix}_lowRisk`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: "High Risk",
              field: `${monthPrefix}_highRisk`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: "Mark up (Historical)",
              field: `${monthPrefix}_markupHistorical`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: ` ${year} MTBP `,
              field: `${monthPrefix}_mtbp2025`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: `vs ${year} MTBP`,
              field: `${monthPrefix}_vsMtbp2025`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: " FC6 ",
              field: `${monthPrefix}_fc6`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: "vs FC6",
              field: `${monthPrefix}_vsFC6`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: `Apr-${String(year - 1).slice(-2)}`,
              field: `${monthPrefix}_apr24`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
            {
              headerName: `vs ${String(year - 1).slice(-2)}A`,
              field: `${monthPrefix}_vs24A`,
              columnGroupShow: "open",
              editable: true,
              cellEditor: "agNumberCellEditor",
              flex: 1,
              minWidth: 100,
            },
          ],
        });
      });
    });
    
    return allMonthColumns;
  };

  const columnDefs = useMemo(() => [
    {
      headerName: "",
      children: [
        {
          headerName: "SBU Code",
          field: "sbucode",
          width: 180,
          editable: false,
          pinned: 'left',
          rowSpan: (params) => params.data?.rowSpan || 1,
          cellClassRules: {
            "row-span-cell": (p) => (p.data?.rowSpan || 1) > 1,
          },
        },
        {
          headerName: "Item Type",
          field: "itemType",
          width: 120,
          editable: false,
        },
      ],
    },
    ...getQuarterColumns(selectedQuarter, selectedYear),
  ], [selectedQuarter, selectedYear]);

  /* ===============================
     SAVE ON CELL EDIT
  =============================== */
  const onCellValueChanged = useCallback((params) => {
    setGridRowData((prev) =>
      prev.map((row, index) => {
        if (index === params.rowIndex) {
          return {
            ...row,
            [params.colDef.field]: params.newValue,
          };
        }
        return row;
      })
    );

    // 🔵 API call can be placed here
    console.log("Row saved:", params.data);
  }, []);

  /* ===============================
     MANUAL SAVE (OPTIONAL)
  =============================== */
  const saveAllRows = () => {
    console.log("Saving all rows to backend:", gridRowData);

    // Example:
    // axios.post("/api/save", gridRowData);
  };

  /* ===============================
     RENDER
  =============================== */

const onColumnGroupOpened = useCallback((event) => {
  const api = gridRef.current.api;
  const clickedGroupId = event.columnGroup.getGroupId();

  // Close ALL groups first
  api.getColumnGroupState().forEach((group) => {
    api.setColumnGroupOpened(group.groupId, false);
  });

  // 🔑 Explicitly open the clicked group
  api.setColumnGroupOpened(clickedGroupId, true);
}, []);


  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%' }}>
        {/* Controls Row */}
        <div style={{ padding: '15px 20px', display: 'flex', gap: '15px', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div>
              <label style={{ marginRight: '8px', fontWeight: 'bold' }}>Year:</label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ marginRight: '8px', fontWeight: 'bold' }}>Quarter:</label>
              <Select
                mode="multiple"
                placeholder="Select Quarter(s)"
                value={selectedQuarter}
                onChange={(values) => setSelectedQuarter(values)}
                style={{ width: 250 }}
                options={[
                  { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                  { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                  { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                  { label: 'Q4 (Jan-Mar)', value: 'Q4' },
                ]}
              />
            </div>
          </div>

          <button onClick={saveAllRows} style={{
            padding: '8px 16px',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }} className="bg-[#287DD1]">Save All Changes</button>
        </div>

        {/* Grid Container - Fills remaining space */}
        <div className="ag-theme-alpine" style={{ flex: 1, width: "100%", border: '1px solid #d0d0d0', maxHeight: '600px', overflow: 'auto', boxSizing: 'border-box' }}>
          <AgGridReact
            ref={gridRef}
            rowData={gridRowData}
            columnDefs={columnDefs}
            suppressRowTransform={true}
            onCellValueChanged={onCellValueChanged}
            stopEditingWhenCellsLoseFocus={true}
            singleClickEdit={true}
            suppressSortingAndFiltering={true}
            domLayout="normal"
            suppressHorizontalScroll={false}
            defaultColDef={{
              resizable: true,
            }}
            rowHeight={38}
            headerHeight={38}
            wrapHeaderText={true}
            autoHeaderHeight={true}
            icons={{
              columnGroupClosed: '+',
              columnGroupOpened: '−',
            }}
          />
        </div>
      </div>
    </>
  );
};

export default EditableRowSpanWithColumnGroup;
